<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dateFormat } from 'shared/helpers/timeHelper';
import { BaseTableRow, BaseTableCell } from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
  isCloning: {
    type: Boolean,
    default: false,
  },
  isExporting: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['delete', 'clone', 'export']);
const { t } = useI18n();

const toolsCount = computed(() => props.server.tools?.length ?? 0);

const updatedAt = computed(() =>
  dateFormat(new Date(props.server.updated_at).getTime() / 1000, 'LLL d, yyyy')
);

const updatedAtWithTime = computed(() =>
  dateFormat(
    new Date(props.server.updated_at).getTime() / 1000,
    'LLL d, yyyy hh:mm a'
  )
);

const statusClasses = computed(() =>
  props.server.active
    ? 'text-n-teal-11 bg-n-teal-3'
    : 'text-n-slate-11 bg-n-alpha-2'
);
</script>

<template>
  <BaseTableRow :item="server">
    <template #default>
      <BaseTableCell class="max-w-0 w-full">
        <div class="flex flex-col items-start gap-1">
          <span class="block text-heading-3 text-n-slate-12 truncate">
            {{ server.name }}
          </span>
          <p class="mb-0 text-n-slate-11 text-body-main line-clamp-2">
            {{ server.description }}
          </p>
        </div>
      </BaseTableCell>

      <BaseTableCell>
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ t('WGPT_MCP_SERVERS.LIST.TOOLS_COUNT', { count: toolsCount }) }}
        </span>
      </BaseTableCell>

      <BaseTableCell>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 rounded-md"
          :class="statusClasses"
        >
          {{
            server.active
              ? t('WGPT_MCP_SERVERS.LIST.STATUS.ACTIVE')
              : t('WGPT_MCP_SERVERS.LIST.STATUS.INACTIVE')
          }}
        </span>
      </BaseTableCell>

      <BaseTableCell :title="updatedAtWithTime">
        <span class="text-body-main text-n-slate-12 whitespace-nowrap">
          {{ updatedAt }}
        </span>
      </BaseTableCell>

      <BaseTableCell align="end">
        <div class="flex gap-3 justify-end flex-shrink-0">
          <router-link
            :to="{
              name: 'wgpt_mcp_servers_edit',
              params: { serverId: server.id },
            }"
          >
            <Button
              v-tooltip.top="$t('WGPT_MCP_SERVERS.LIST.TOOLTIP.SETTINGS')"
              icon="i-woot-edit-pen"
              sm
              slate
            />
          </router-link>
          <Button
            v-tooltip.top="$t('WGPT_MCP_SERVERS.LIST.TOOLTIP.CLONE')"
            icon="i-woot-clone"
            slate
            sm
            :disabled="isCloning"
            @click="$emit('clone')"
          />
          <Button
            v-tooltip.top="$t('WGPT_MCP_SERVERS.LIST.TOOLTIP.EXPORT')"
            icon="i-lucide-upload"
            slate
            sm
            :disabled="isExporting"
            :is-loading="isExporting"
            @click="$emit('export')"
          />
          <Button
            v-tooltip.top="$t('WGPT_MCP_SERVERS.LIST.TOOLTIP.DELETE')"
            icon="i-woot-bin"
            slate
            sm
            class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
            @click="$emit('delete')"
          />
        </div>
      </BaseTableCell>
    </template>
  </BaseTableRow>
</template>
